import { LockOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Timer, PayoutOrderStatusLabel } from 'components';
import {
  HIDE_PAYOUT_TIMER_IN_STATUSES,
  MANUAL_PAYOUT_STATUS_DETAILS,
} from 'constants/order.constants';
import { PayoutManualStatusDetails, PayoutOrderStatus } from 'enums';
import { useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PayoutOrder } from 'types';
import { orderUtils } from 'utils';

type Props = {
  order: PayoutOrder;
};

export const PayoutOrderStatusColumn: React.FC<Props> = ({ order }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.payout_orders.labels',
  });
  const { t: tStatusDetails } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'payout_manual_status_details',
  });

  const { role } = useUser();
  const displayState = useMemo(
    () => orderUtils.getPayoutOrderDisplayState(order, role),
    [order, role],
  );

  const lockTooltip = useMemo(() => {
    if (order.lock?.lockReason) {
      return t(`lock_reason.${order.lock.lockReason}`, {
        defaultValue: order.lock.lockReason,
      });
    }
    return t('locked');
  }, [order.lock, t]);

  // An order parked for manual distribution stays in `new`; what tells the operator where it
  // actually is, is the sub-state.
  const manualStatusDetails = useMemo(
    () =>
      displayState.status === PayoutOrderStatus.New &&
      MANUAL_PAYOUT_STATUS_DETAILS.includes(
        displayState.statusDetails as PayoutManualStatusDetails,
      )
        ? (displayState.statusDetails as PayoutManualStatusDetails)
        : null,
    [displayState.status, displayState.statusDetails],
  );

  return (
    <div>
      {!HIDE_PAYOUT_TIMER_IN_STATUSES.includes(order.status) &&
        (order.currentCascadeAssigneeTimeoutAt &&
        order.status === PayoutOrderStatus.Requisites ? (
          <Timer
            hideTimeOver
            endTime={orderUtils.getOrderCurrentCascadeAssigneeTimeoutAt(order)}
          />
        ) : (
          <Timer hideTimeOver endTime={orderUtils.getMaxTimeoutAt(order)} />
        ))}
      <div>
        <div className="tw-flex tw-items-center tw-gap-1">
          {order.lock && (
            <Tooltip title={lockTooltip}>
              <LockOutlined sx={{ fontSize: 16, color: 'error.main' }} />
            </Tooltip>
          )}
        </div>
        {/* The sub-state already implies `new`, so showing both just repeats the same word. */}
        {manualStatusDetails ? (
          <div className="tw-text-amber-600">
            {tStatusDetails(manualStatusDetails)}
          </div>
        ) : (
          <PayoutOrderStatusLabel status={displayState.status} />
        )}
      </div>
    </div>
  );
};
